/**
 * serviceOrderDiagnosis — o bloco "Diagnóstico técnico" da Ordem de Serviço.
 *
 * Três campos de texto livre, na OS e **não** no orçamento: o orçamento é uma
 * proposta (antes), a OS é a execução (durante/depois). Quem conserta precisa
 * registrar a queixa do cliente, o que constatou e o que fez.
 *
 * ─── AS DUAS REGRAS QUE QUEBRAM EM SILÊNCIO ─────────────────────────────────
 *
 * 1. **Ausência ≠ vazio.** `null` é "o prestador não usa este bloco"; `''` seria
 *    "usou e apagou". A gravação normaliza string em branco para `null`
 *    (`toDiagnosisColumns`) para não encher o banco de strings vazias que
 *    fariam o PDF imprimir um título de seção sem conteúdo. É o mesmo cuidado
 *    de `products.cost` (CLAUDE.md #61): ausência tem significado próprio.
 *
 * 2. **Escrita E leitura.** `service_orders` é gravada por LISTA EXPLÍCITA de
 *    colunas em quatro lugares (o hook online, o executor offline, o cache e o
 *    formulário). Acrescentar coluna só na migration + no formulário faz o save
 *    "dar certo" e o valor nunca chegar ao banco — foi exatamente o que
 *    aconteceu com `companies.signature_url` (#33) e com a view do financeiro
 *    (#79). Por isso existe o par builder/leitor abaixo, e um guard de
 *    varredura cobrando os DOIS sentidos (#70).
 */

export interface ServiceOrderDiagnosis {
  defect: string | null;
  diagnosis: string | null;
  solution: string | null;
}

/** As três chaves, na ordem em que aparecem na tela e no PDF. */
export const DIAGNOSIS_FIELDS = [
  {
    key: 'defect',
    label: 'Defeito relatado',
    placeholder: 'O que o cliente descreveu. Ex.: não liga, faz barulho ao ligar',
  },
  {
    key: 'diagnosis',
    label: 'Diagnóstico técnico',
    placeholder: 'O que você constatou na inspeção. Ex.: fonte queimada, capacitor estufado',
  },
  {
    key: 'solution',
    label: 'Solução aplicada',
    placeholder: 'O que foi feito. Ex.: troca da fonte e limpeza interna',
  },
] as const satisfies readonly { key: keyof ServiceOrderDiagnosis; label: string; placeholder: string }[];

function normalize(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text.length > 0 ? text : null;
}

/**
 * Monta as 3 colunas de uma vez — o payload de gravação nunca deve enumerá-las
 * à mão, senão um caminho esquece uma e o dado some sem erro.
 */
export function toDiagnosisColumns(input: Partial<Record<keyof ServiceOrderDiagnosis, unknown>> | null | undefined): ServiceOrderDiagnosis {
  return {
    defect: normalize(input?.defect),
    diagnosis: normalize(input?.diagnosis),
    solution: normalize(input?.solution),
  };
}

/** O leitor: extrai o bloco de uma linha vinda do servidor ou do cache. */
export function diagnosisFromRow(row: Record<string, unknown> | null | undefined): ServiceOrderDiagnosis {
  return toDiagnosisColumns(row ?? undefined);
}

/** Tem algo para mostrar? Usado pela tela e pelo PDF para omitir o bloco todo. */
export function hasDiagnosis(value: ServiceOrderDiagnosis | null | undefined): boolean {
  if (!value) return false;
  return Boolean(value.defect || value.diagnosis || value.solution);
}

/** Só as linhas preenchidas, prontas para renderizar (tela ou PDF). */
export function diagnosisEntries(
  value: ServiceOrderDiagnosis | null | undefined,
): { key: keyof ServiceOrderDiagnosis; label: string; text: string }[] {
  if (!value) return [];
  const out: { key: keyof ServiceOrderDiagnosis; label: string; text: string }[] = [];
  for (const field of DIAGNOSIS_FIELDS) {
    const text = value[field.key];
    if (text) out.push({ key: field.key, label: field.label, text });
  }
  return out;
}
